import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderInfo, values } from '../data/mock';
import { Shield, Heart, Eye, Clock, Users, Sparkles } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/bb5f3c6757d7d042a11246056b3090524e1c5974db1b3942804fa38cf37ab5b9.png";
const STORY_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/543075ead14fb84b02e7344d90d965ec4cb236b345428732cf10d9a17dd8ae3a.png";

const valueIcons = [Shield, Clock, Heart, Eye, Sparkles, Users];

export const About = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="about-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="about-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.9) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(194,168,120,0.06) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '180px', paddingBottom: '180px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '36px' }}>
              About Us
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="about-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(40px, 5.5vw, 68px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '32px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              A Chauffeur Service with a Human Touch
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '18px', color: 'rgba(220,220,220,0.65)', fontWeight: 300, lineHeight: 1.8, maxWidth: '520px', margin: '0 auto' }}>
              Built on integrity, driven by genuine care
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '160px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── FOUNDER STORY — Cinematic Full-Width with Moving Background ── */}
      <section data-testid="founder-story" className="relative overflow-hidden" style={{ minHeight: '700px' }}>
        {/* Cinematic moving background */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${STORY_IMG})`,
          filter: 'brightness(0.30) saturate(0.7) contrast(1.08)',
          animation: 'cinematicPan 30s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.45) 25%, rgba(10,10,10,0.45) 75%, rgba(10,10,10,0.9) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-4xl mx-auto relative z-10" style={{ padding: '10rem 1.5rem' }}>
          {/* Glass-morphism content card */}
          <div className="fade-element" style={{
            backgroundColor: 'rgba(14,14,14,0.55)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(194,168,120,0.08)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(194,168,120,0.04)',
            padding: 'clamp(48px, 6vw, 80px)',
            position: 'relative',
          }}>
            {/* Gold top accent */}
            <div style={{
              position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.15), transparent)',
            }} />

            <div className="text-center" style={{ marginBottom: '48px' }}>
              <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '24px' }}>
                Our Story
              </p>
              <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.5 }} />
              <h2 data-testid="about-founder-heading" className="font-serif" style={{
                fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 400, color: '#F5F5F5', marginBottom: '0', lineHeight: 1.2,
              }}>
                Why Toronto Royale Exists
              </h2>
            </div>

            <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 2, marginBottom: '40px', textAlign: 'center', maxWidth: '640px', margin: '0 auto 40px' }}>
              {founderInfo.story}
            </p>

            <div style={{
              borderLeft: '2px solid rgba(194,168,120,0.2)', paddingLeft: '28px', margin: '0 auto 40px',
              maxWidth: '560px',
            }}>
              <p className="font-serif" style={{ fontSize: '18px', fontStyle: 'italic', color: 'rgba(220,220,220,0.5)', lineHeight: 1.8 }}>
                {founderInfo.mission}
              </p>
            </div>

            <p style={{ fontSize: '14px', color: 'rgba(204,204,204,0.45)', fontWeight: 300, lineHeight: 2, textAlign: 'center', maxWidth: '580px', margin: '0 auto 36px' }}>
              Today, Toronto Royale serves executives, families, and travellers across the Greater Toronto Area. We're not the biggest service — but we're proud to be one built on relationships, not transactions.
            </p>

            <div className="flex items-center justify-center" style={{ gap: '12px' }}>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.2)' }} />
              <span style={{ fontSize: '12px', color: 'rgba(194,168,120,0.45)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 300 }}>
                {founderInfo.name}
              </span>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.2)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{ width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08) 20%, rgba(194,168,120,0.08) 80%, transparent)' }} />

      {/* ── WHAT MAKES US DIFFERENT — Glass-Morphism Cards ── */}
      <section data-testid="why-different" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="absolute inset-0" style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.10) saturate(0.45)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.98) 0%, rgba(10,10,10,0.85) 30%, rgba(10,10,10,0.85) 70%, rgba(10,10,10,0.98) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 70% 30%, rgba(194,168,120,0.04) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.2) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.03 }} />

        <div className="max-w-5xl mx-auto relative z-10" style={{ padding: '10rem 1.5rem' }}>
          <div className="text-center fade-element" style={{ marginBottom: '80px' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              What Makes Us Different
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(225,225,225,0.8)', maxWidth: '500px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              In a world of fleet operators and ride-hailing apps, we chose a different path.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: '24px' }}>
            {[
              {
                title: "We're Not a Fleet",
                text: "We're a boutique service. You won't get a different driver every time. You'll build a relationship with someone who knows your preferences, respects your time, and genuinely cares about your experience."
              },
              {
                title: "We're Founder-Led",
                text: "This isn't a corporate operation. Every decision, every standard, every interaction reflects our founder's personal commitment to hospitality and integrity."
              },
              {
                title: "We're Human",
                text: "We listen. We adapt. We remember. This is hospitality, not logistics. You're not a booking number \u2014 you're a valued client who deserves care and respect."
              }
            ].map((item, i) => (
              <div
                key={i}
                className="fade-element"
                style={{
                  padding: '48px 36px',
                  backgroundColor: 'rgba(14,14,14,0.5)',
                  backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(194,168,120,0.06)',
                  transition: 'border-color 600ms ease, box-shadow 600ms ease, transform 400ms ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(194,168,120,0.18)';
                  e.currentTarget.style.boxShadow = '0 16px 56px rgba(0,0,0,0.35)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(194,168,120,0.06)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.15), transparent)',
                }} />
                <div style={{ width: '1px', height: '28px', backgroundColor: 'rgba(194,168,120,0.2)', marginBottom: '24px' }} />
                <h3 className="font-serif" style={{ fontSize: '20px', fontWeight: 400, color: '#F5F5F5', marginBottom: '16px' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.9, fontWeight: 300 }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VALUES — Cinematic Grid with Glass ── */}
      <section data-testid="values-section" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A', padding: '10rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 70%, rgba(194,168,120,0.03) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 80% 20%, rgba(194,168,120,0.02) 0%, transparent 50%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.03 }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '80px' }}>
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '24px' }}>
              Our Values
            </p>
            <h2 className="font-serif" style={{
              fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              What We Stand For
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px', opacity: 0.5 }} />
            <p style={{ fontSize: '16px', color: 'rgba(225,225,225,0.8)', maxWidth: '520px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              These principles guide every journey, every interaction, every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '24px' }}>
            {values.map((value, i) => {
              const Icon = valueIcons[i] || Shield;
              return (
                <div
                  key={i}
                  className="fade-element"
                  style={{
                    padding: '40px 32px',
                    backgroundColor: 'rgba(14,14,14,0.4)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(194,168,120,0.06)',
                    transition: 'border-color 600ms ease, box-shadow 600ms ease, transform 400ms ease',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.16)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.25)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.06)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 0, left: '20%', right: '20%', height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.1), transparent)',
                  }} />
                  <div style={{
                    width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.08)',
                    marginBottom: '24px',
                  }}>
                    <Icon size={18} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
                  </div>
                  <h3 className="font-serif" style={{ fontSize: '18px', fontWeight: 400, color: 'rgba(194,168,120,0.8)', marginBottom: '12px' }}>
                    {value.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(204,204,204,0.45)', lineHeight: 1.8, fontWeight: 300 }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── OLOA Credentials ── */}
      <section data-testid="about-oloa" className="relative" style={{ backgroundColor: '#0A0A0A', padding: '6rem 1.5rem 7rem' }}>
        <div className="max-w-3xl mx-auto text-center fade-element">
          <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.5 }} />
          <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '20px' }}>
            Credentials & Accreditation
          </p>
          <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', fontWeight: 500, color: '#FAFAFA', marginBottom: '24px', lineHeight: 1.2 }}>
            Proud Member — National & Ontario Limousine Associations
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 1.9, maxWidth: '640px', margin: '0 auto 48px' }}>
            Toronto Royale is a proud member of the <strong style={{ color: '#C2A878', fontWeight: 500 }}>NLA — National Limousine Association</strong>, the industry's leading body representing professional chauffeured transportation across North America, and a member in good standing of the <strong style={{ color: '#C2A878', fontWeight: 500 }}>OLOA — Ontario Limousine Owners Association</strong>. These accreditations signal our commitment to the highest professional standards — full commercial licensing, comprehensive insurance, vetted chauffeurs, and industry-wide best practices.
          </p>

          <div className="flex flex-col md:flex-row items-stretch justify-center" style={{ gap: '24px' }}>
            {/* NLA — official Proud Member badge (links to membership certificate) */}
            <a
              href="https://www.limo.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-nla-badge"
              className="trust-badge"
              aria-label="Visit the National Limousine Association official website (opens in a new tab)"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '0',
                padding: '0',
                border: '1px solid rgba(194,168,120,0.18)',
                background: 'rgba(14,14,14,0.5)',
                backdropFilter: 'blur(12px)',
                flex: '1 1 0',
                maxWidth: '320px',
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              <div style={{ background: '#FFFFFF', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 24px' }}>
                <img
                  src="/badges/nla-proud-member-2025.png"
                  alt="National Limousine Association — Proud Member 2025"
                  width="190"
                  height="190"
                  loading="lazy"
                  decoding="async"
                  style={{ width: '190px', height: 'auto', display: 'block' }}
                />
              </div>
              <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, textAlign: 'center', padding: '18px 16px 16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Visit National Limousine Association
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 17 17 7" /><path d="M7 7h10v10" />
                </svg>
              </p>
            </a>

            {/* OLOA — text lockup badge (links to official OLOA site) */}
            <a
              href="https://www.oloa.ca/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-oloa-badge"
              className="trust-badge"
              aria-label="Visit the Ontario Limousine Owners Association official website (opens in a new tab)"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '18px',
                padding: '36px 48px',
                border: '1px solid rgba(194,168,120,0.18)',
                background: 'rgba(14,14,14,0.5)',
                backdropFilter: 'blur(12px)',
                flex: '1 1 0',
                maxWidth: '320px',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span className="font-serif" style={{ fontSize: '32px', color: '#C2A878', letterSpacing: '6px', fontWeight: 500 }}>OLOA</span>
                <span style={{ width: '1px', height: '30px', background: 'rgba(194,168,120,0.3)' }} />
                <div style={{ textAlign: 'left' }}>
                  <p style={{ fontSize: '11px', color: 'rgba(220,220,220,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400 }}>Ontario Limousine</p>
                  <p style={{ fontSize: '11px', color: 'rgba(220,220,220,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400 }}>Owners Association</p>
                </div>
              </div>
              <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Visit oloa.ca
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 17 17 7" /><path d="M7 7h10v10" />
                </svg>
              </p>
            </a>
          </div>

          <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <a
              href="/toronto-royale-credentials.pdf"
              download
              data-testid="about-credentials-download"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: 'transparent',
                color: '#C2A878',
                padding: '14px 32px',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                border: '1px solid rgba(194,168,120,0.35)',
                textDecoration: 'none',
                transition: 'all 320ms cubic-bezier(0.22,1,0.36,1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.08)';
                e.currentTarget.style.borderColor = 'rgba(194,168,120,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(194,168,120,0.35)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Credentials & Trust PDF
            </a>
            <p style={{ fontSize: '11px', color: 'rgba(225,225,225,0.75)', fontWeight: 300, letterSpacing: '0.3px' }}>
              One-page document for corporate procurement teams
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA — Cinematic ── */}
      <section data-testid="about-cta" className="relative overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.15) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.55) 35%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.06) 0%, transparent 45%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.2) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-3xl mx-auto text-center relative z-10" style={{ padding: '14rem 1.5rem' }}>
          <div className="fade-element">
            <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(32px, 4.5vw, 54px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', lineHeight: 1.12, textShadow: '0 2px 30px rgba(0,0,0,0.4)',
            }}>
              Experience the Toronto Royale Difference
            </h2>
            <p style={{
              fontSize: '17px', color: 'rgba(220,220,220,0.5)', fontWeight: 300,
              lineHeight: 1.8, maxWidth: '500px', margin: '0 auto 52px',
            }}>
              Let us show you what founder-led, relationship-driven service truly means.
            </p>
            <div className="flex flex-wrap justify-center" style={{ gap: '16px' }}>
              <button
                data-testid="about-cta-btn"
                onClick={() => navigate('/contact')}
                style={{
                  backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 40px',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                  boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
              >
                Get in Touch
              </button>
              <button
                data-testid="about-chauffeur-btn"
                onClick={() => navigate('/chauffeur')}
                style={{
                  backgroundColor: 'transparent', color: '#C2A878', padding: '15px 40px',
                  fontSize: '12px', fontWeight: 400, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: '1px solid rgba(194,168,120,0.3)', cursor: 'pointer', transition: 'all 400ms ease',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.3)'; }}
              >
                Chauffeur Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
        @keyframes cinematicPan {
          0% { transform: scale(1.15) translateX(-2%); }
          50% { transform: scale(1.2) translateX(2%); }
          100% { transform: scale(1.15) translateX(-2%); }
        }
      `}</style>
    </div>
  );
};
